import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import AutoCompleteSingle from '../../common/AutoCompleteSingle';
import AutoCompleteTaggedField from '../../common/AutoCompleteTaggedField';
import { STATUS } from './redux/constants';
import FolderIcon from '../../icons/folder.svg';
import GlobeIcon from '../../icons/globe.svg';
import LoadingComponent from '../../common/LoadingComponent';
import {
    // Projects
    getAllProjectsRequested,
    setProject,
    // Locations (formerly Places)
    getAllPlacesRequested,
    setPlaces,
    setSelectedPlacesAction,
    // Zones
    getAllZonesRequested,
    setZone,
    // Researchers
    researchersRequested,
    setSelectedResearchersAction,
    // Status
    setStatus,
    // Results
    getPlacesRequested,
} from './redux/actions';
import {
    // Projects
    getAllProjectsState,
    getAllProjectsLoading,
    getSelectedProject,
    // Locations (formerly Places)
    getAllPlacesState,
    // Zones
    getAllZonesState,
    getAllZonesLoading,
    getSelectedZone,
    // Researchers
    getResearchers,
    getResearchError,
    // Status
    getStatus,
    // Results
    getPlacesLoading,
} from './redux/selectors';
// import { noop } from '../../common/propTypes';

function ResearchForm({
    // Projects
    fetchAllProjects,
    isLoadingProjects,
    allProjects,
    setProject,
    selectedProject,

    // Locations (formerly Places)
    fetchAllPlaces,
    allPlaces,
    setPlaces,
    setSelectedPlacesStore,

    // Zones
    fetchAllZones,
    isLoadingZones,
    allZones,
    setZone,
    selectedZone,

    // Researchers
    researchers,
    fetchResearchers,
    setSelectedResearchersStore,

    // Status
    status,
    setStatus,

    // Results
    fetchPlaces,
    isLoadingSearchResults,
    error,
}) {
    const [searchClicked, setSearchClicked] = useState(false);
    const [selectedPlaces, setSelectedPlaces] = useState(null);
    const [selectedResearchers, setSelectedResearchers] = useState(null);

    useEffect(() => {
        fetchAllProjects();
        fetchResearchers();
    }, []);

    useEffect(() => {
        // Pre-select the first Project
        // When a new Project is created, it will be added first in the state, to be selected automatically.
        if (allProjects && allProjects[0]) {
            handleSelectProject(allProjects[0]);
            // Clear any search results
            setPlaces([]);
            setSearchClicked(false);
        }
    }, [allProjects]);

    function clearSelection(id, valueSetterFunction) {
        clearAutocompleteState(id);
        valueSetterFunction(null);
    }

    function clearAutocompleteState(id) {
        try {
            document
                .getElementById(id) // call clear event of AutoComplete field
                .parentNode.getElementsByClassName(
                    'MuiAutocomplete-clearIndicator',
                )[0]
                .click();
        } catch (e) {
            Sentry.captureException(e);
            // console.log(e);
        }
    }

    const resetFilters = () => {
        clearSelection('research-project-autocomplete', setProject);
        clearSelection('researchers-autocomplete', setSelectedResearchers);
        clearSelection('research-locations-autocomplete', setSelectedPlaces);
        clearSelection('research-zone-autocomplete', setZone);
        setStatus(STATUS[0]);
        //Results
        setPlaces([]);
        setSearchClicked(false);
    };

    const handleSelectProject = (project) => {
        setProject(project);
        clearSelection('research-locations-autocomplete', setSelectedPlaces);
        clearSelection('research-zone-autocomplete', setZone);
        // Fetch all Locations and Zones for drop-downs when a Project is selected, but NOT when the Project drop-down is cleared/reset.
        // Otherwise will perform wasteful query for all Places, while the Locations drop-down is disabled.
        if (project) {
            fetchAllPlaces();
            fetchAllZones();
        }
    };

    const handleSearch = () => {
        setSelectedPlacesStore((selectedPlaces || []).map((item) => item.id));
        setSelectedResearchersStore(
            (selectedResearchers || []).map((item) => item.id),
        );
        setSearchClicked(true);
        fetchPlaces();
    };

    const loader = (
        <div className="buttonLoader" style={{ minWidth: 'unset' }}>
            <LoadingComponent isLoading={true} />
        </div>
    );
    return (
        <div>
            <div className="col-md-12">
                <div className="content-box">
                    {error && (error || '').includes('not allowed') ? (
                        <>{error}</>
                    ) : (
                        <>
                            <div className="row">
                                <div className="col-6">
                                    <AutoCompleteSingle
                                        id="research-project-autocomplete"
                                        title="Project Name"
                                        bootstrapIcon={FolderIcon}
                                        stylesIcon={{
                                            justifyContent: 'center',
                                        }}
                                        placeholder={'Select Project'}
                                        isLoading={isLoadingProjects}
                                        noOptionsText={'No Projects Found'}
                                        options={allProjects}
                                        getOptionLabel={(option) =>
                                            `${option.name}`
                                        }
                                        renderOption={(option) => option.name}
                                        value={selectedProject}
                                        onSelect={handleSelectProject}
                                    />
                                </div>

                                <div className="col-6">
                                    <AutoCompleteTaggedField
                                        bootstrapIcon="icon-latest-multiple-user"
                                        id="researchers-autocomplete"
                                        title="Researchers"
                                        placeholder={`${
                                            selectedResearchers &&
                                            selectedResearchers.length
                                                ? ''
                                                : 'Select Researchers'
                                        }`}
                                        options={researchers}
                                        defaultValue={selectedResearchers}
                                        onSelect={setSelectedResearchers}
                                    />
                                </div>

                                <div className="col-6">
                                    <AutoCompleteTaggedField
                                        bootstrapIcon="icon-location p-2"
                                        id="research-locations-autocomplete"
                                        title="Locations"
                                        disabled={!selectedProject}
                                        placeholder={`${
                                            selectedPlaces &&
                                            selectedPlaces.length
                                                ? ''
                                                : 'Select Locations'
                                        }`}
                                        options={allPlaces}
                                        defaultValue={selectedPlaces}
                                        onSelect={setSelectedPlaces}
                                    />
                                </div>

                                <div className="col-6">
                                    <AutoCompleteSingle
                                        id="research-zone-autocomplete"
                                        title="Zone"
                                        bootstrapIcon={GlobeIcon}
                                        stylesIcon={{
                                            justifyContent: 'center',
                                        }}
                                        disabled={!selectedProject}
                                        placeholder={`${
                                            selectedZone && selectedZone.length
                                                ? ''
                                                : 'Select Zone'
                                        }`}
                                        isLoading={isLoadingZones}
                                        noOptionsText={'No Zones Found'}
                                        options={allZones}
                                        renderOption={(option) => option.name}
                                        value={selectedZone}
                                        onSelect={setZone}
                                    />
                                </div>

                                <div className="col-6">
                                    <h3 className="titleStyle2">Status</h3>
                                    {STATUS.map((item) => {
                                        return (
                                            <span
                                                className="status-container"
                                                key={item.id}
                                            >
                                                <input
                                                    type="radio"
                                                    id={item.id}
                                                    value={item.value}
                                                    onChange={() =>
                                                        setStatus(item)
                                                    }
                                                    checked={
                                                        item.id == status.id
                                                    }
                                                />
                                                <label htmlFor={item.id}>
                                                    {item.value}
                                                </label>
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-6">
                                    <button
                                        disabled={!selectedProject}
                                        className="btn btn-custom btn-lg mt-3"
                                        onClick={handleSearch}
                                    >
                                        {isLoadingSearchResults
                                            ? loader
                                            : 'Search'}
                                    </button>
                                    {searchClicked && (
                                        <button
                                            onClick={resetFilters}
                                            className="btn btn-custom reset-btn btn-lg mt-3"
                                        >
                                            Reset
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

ResearchForm.propTypes = {
    // Projects
    fetchAllProjects: PropTypes.func,
    isLoadingProjects: PropTypes.bool,
    allProjects: PropTypes.array,
    setProject: PropTypes.func,
    selectedProject: PropTypes.object,
    // Locations (formerly Places)
    fetchAllPlaces: PropTypes.func,
    allPlaces: PropTypes.array,
    setSelectedPlacesStore: PropTypes.func,
    setPlaces: PropTypes.func,
    // Zones
    fetchAllZones: PropTypes.func,
    isLoadingZones: PropTypes.bool,
    allZones: PropTypes.array,
    setZone: PropTypes.func,
    selectedZone: PropTypes.object,
    // Researchers
    fetchResearchers: PropTypes.func,
    setSelectedResearchersStore: PropTypes.func,
    researchers: PropTypes.array,
    // Status
    status: PropTypes.object,
    setStatus: PropTypes.func,
    // Results
    fetchPlaces: PropTypes.func,
    isLoadingSearchResults: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

ResearchForm.defaultProps = {
    isLoadingProjects: false,
    isLoadingZones: false,
    isLoadingSearchResults: false,
    error: null,
};

const mapStateToProps = (state) => {
    return {
        // Projects
        allProjects: getAllProjectsState(state),
        isLoadingProjects: getAllProjectsLoading(state),
        selectedProject: getSelectedProject(state),
        // Locations (formerly Places)
        allPlaces: getAllPlacesState(state),
        // Zones
        allZones: getAllZonesState(state),
        isLoadingZones: getAllZonesLoading(state),
        selectedZone: getSelectedZone(state),
        // Researchers
        researchers: getResearchers(state),
        // Status
        status: getStatus(state),
        // Results
        isLoadingSearchResults: getPlacesLoading(state),
        error: getResearchError(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        // Projects
        fetchAllProjects: () => dispatch(getAllProjectsRequested()),
        setProject: (project) => dispatch(setProject(project)),
        // Locations (formerly Places)
        fetchAllPlaces: () => dispatch(getAllPlacesRequested()),
        setPlaces: (places) => dispatch(setPlaces(places)),
        setSelectedPlacesStore: (places) =>
            dispatch(setSelectedPlacesAction(places)),
        // Zones
        fetchAllZones: () => dispatch(getAllZonesRequested()),
        setZone: (zone) => dispatch(setZone(zone)),
        // Researchers
        fetchResearchers: () => dispatch(researchersRequested()),
        setSelectedResearchersStore: (researchers) =>
            dispatch(setSelectedResearchersAction(researchers)),
        // Status
        setStatus: (status) => dispatch(setStatus(status)),
        //Results
        fetchPlaces: () => dispatch(getPlacesRequested()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResearchForm);
